import React from 'react';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';

/**
 * THE ACTIVE SYMBOL, beside the brand in the app bar: {@code integralDelta.ai | SPY}.
 *
 * <h2>⚠ A COMPONENT RATHER THAN JSX INLINE IN App.js, so it can be RENDER-tested</h2>
 *
 * The header renders twice — an editable Box for an admin, an inert Typography for a customer —
 * and this goes in both, so it has to be defined once either way: duplicated markup would be two
 * places for one to be forgotten, which is the argument the area-highlight pills beside the brand
 * already make in their own comment.
 *
 * <p>Its own module because the alternative was a `const` in a 7,600-line component, which nothing
 * can import. Every existing test of the header reads App.js as TEXT for exactly that reason, and
 * a source grep cannot see whether an element renders — the two defects this session found in
 * chart marks were both an element that was present in the option and invisible on screen.
 *
 * <h2>⚠ NOT `color: 'text.secondary'`, WHICH IS INVISIBLE ON THIS BAR</h2>
 *
 * The obvious styling for a secondary label, and it is wrong here: the bar is
 * {@code <AppBar color="primary">}, so its text is {@code primary.contrastText} while
 * {@code text.secondary} resolves against the PAGE palette. Measured against each shipped primary:
 *
 * <pre>
 *   bar              brand   text.secondary
 *   light  #1976d2    4.60   #555    -> 1.62
 *   dark   #58a6ff    7.49   #9aa7b4 -> 1.03   <-- indistinguishable from the bar
 *   preset #38bdf8    2.14   #4f6e8c -> 2.49
 *   preset #e573b5    2.82   #d946ef -> 1.23
 * </pre>
 *
 * It fails 4.5:1 on all four, and vanishes on the dark theme — because the two themes INVERT:
 * light is a dark blue with white text, dark is a light blue with near-black
 * {@code contrastText}. Only an inherited colour tracks that, so the symbol inherits.
 *
 * <h2>⚠ AND IT IS SUBORDINATE BY WEIGHT AND SIZE, NOT BY FADING</h2>
 *
 * The brand's own contrast is 4.60 on the light bar — AA for normal text, with no headroom. A
 * 0.95rem bold label is not WCAG "large" text, so it needs 4.5 too, and fading it to 0.85 opacity
 * takes it to 3.76. So it keeps the full inherited colour and is made secondary by the divider,
 * the smaller size and the letter spacing instead. Fading something while also emboldening it was
 * contradictory anyway.
 *
 * <p>The DIVIDER may be faint: it is {@code aria-hidden} decoration carrying no information, so
 * its contrast is not a reading requirement — and hiding it from the accessibility tree is the
 * point, because "integralDelta.ai vertical bar SPY" is not what a screen reader should say.
 */
const HeaderActiveSymbol = ({ symbol }) => {
  const clean = typeof symbol === 'string' ? symbol.trim() : '';
  // Null rather than an empty element: a stray divider with nothing after it reads as a rendering
  // fault. There is no symbol before the first series loads.
  if (clean === '') return null;
  return (
    <>
      <Typography
        component="span"
        aria-hidden="true"
        sx={{ color: 'inherit', opacity: 0.45, mx: 0.9, fontWeight: 300, userSelect: 'none' }}
      >
        |
      </Typography>
      <Typography
        component="span"
        data-testid="header-active-symbol"
        // `nowrap` because a MIC-suffixed Indian symbol is 13 characters (`RELIANCE.XNSE`) and the
        // brand carries `flexGrow: 1` — wrapping would grow the toolbar rather than the brand's
        // own slack absorbing it.
        sx={{
          color: 'inherit', fontWeight: 700, fontSize: '0.95rem',
          letterSpacing: '0.05em', whiteSpace: 'nowrap',
        }}
      >
        {clean.toUpperCase()}
      </Typography>
    </>
  );
};

HeaderActiveSymbol.propTypes = {
  /** The charted symbol. Absent, empty or whitespace renders nothing. */
  symbol: PropTypes.string,
};

HeaderActiveSymbol.defaultProps = { symbol: '' };

export default HeaderActiveSymbol;
